import {
  ActivateAccountBody,
  AuthMessageResponse,
  ForgotPasswordBody,
  IdentitySelectChallenge,
  IdentitySelectProjectOption,
  ProjectSelectSignInBody,
  ResetPasswordBody,
  SignInBody,
  User,
} from '../types/user';
import baseApi from './baseApi';

export type FilterableUsers = { id: number; name: string; email: string };
type FirebaseTokenResponse = { firebaseToken: string | null };
type RefreshSessionResponse = { accessToken: string; tokenType: string };

const signUpApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    signInUser: build.mutation<User | IdentitySelectChallenge, SignInBody | ProjectSelectSignInBody>({
      query: (body) => ({
        url: '/auth',
        method: 'POST',
        body,
      }),
    }),
    switchProject: build.mutation<User, { projectId: number | null }>({
      query: (body) => ({
        url: '/user/switch-project',
        method: 'POST',
        body,
      }),
    }),
    getIdentityProjects: build.query<{ projects: IdentitySelectProjectOption[] }, void>({
      query: () => ({
        url: '/user/identity-projects',
        method: 'GET',
      }),
    }),
    forgotPassword: build.mutation<AuthMessageResponse, ForgotPasswordBody>({
      query: (body) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: build.mutation<AuthMessageResponse, ResetPasswordBody>({
      query: (body) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body,
      }),
    }),
    activateAccount: build.mutation<AuthMessageResponse, ActivateAccountBody>({
      query: (body) => ({
        url: '/auth/activate',
        method: 'POST',
        body,
      }),
    }),
    refreshSession: build.mutation<RefreshSessionResponse, void>({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
      transformResponse: (response: RefreshSessionResponse): RefreshSessionResponse => response,
    }),
    getUser: build.query({
      query: () => ({
        url: '/user',
        method: 'GET',
      }),
      transformResponse: (response: User): User => response,
      providesTags: ['User'],
    }),
    getUserPermissions: build.query({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      query: (_userId: number | null) => ({
        url: '/user/permissions',
        method: 'GET',
      }),
      transformResponse: (response: { permissions: string[]; hash: string }): { permissions: string[]; hash: string } =>
        response,
      providesTags: ['UserPermissions'],
    }),
    getFilterableUsers: build.query({
      query: (params?: { projectId?: number; onlyActive?: boolean }) => ({
        url: `/user/filterableUsers`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: {
        assignedFilterable: boolean;
        searchableByUser: boolean;
        availableUsers: FilterableUsers[];
      }): {
        assignedFilterable: boolean;
        searchableByUser: boolean;
        availableUsers: FilterableUsers[];
      } => response,
    }),
    registerFcmToken: build.mutation({
      query: (body: { token: string; deviceFingerprint?: string }) => ({
        url: '/user/fcm-token',
        method: 'POST',
        body,
      }),
    }),
    deleteFcmToken: build.mutation({
      query: (body: { token: string }) => ({
        url: '/user/fcm-token/delete',
        method: 'POST',
        body,
      }),
    }),
    reconnectToken: build.mutation<FirebaseTokenResponse, void>({
      query: () => ({
        url: '/user/firebase-token',
        method: 'POST',
      }),
    }),
    logoutUser: build.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useSignInUserMutation,
  useSwitchProjectMutation,
  useGetIdentityProjectsQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useActivateAccountMutation,
  useRefreshSessionMutation,
  useGetUserQuery,
  useGetUserPermissionsQuery,
  useGetFilterableUsersQuery,
  useRegisterFcmTokenMutation,
  useDeleteFcmTokenMutation,
  useReconnectTokenMutation,
  useLogoutUserMutation,
} = signUpApi;
